import * as d3 from 'd3';
import * as d3ease from 'd3-ease';
import {select as d3Select} from 'd3-selection';
import { interpolate as d3Interpolate } from 'd3-interpolate';
import { useLayoutEffect } from 'react';
// Custom hooks
import usePrevious from './usePrevious';
import useAnimated from './useAnimated';
// Constants
import * as constants from './constants';

const arcPathGenerator = d3.arc();

const NodeVisible = ({ person, person_is_noteworthy, radius, radius_off_screen, fill, animating, selected_person_change, initial_animation_delay }) => {

  // Node
  const radius_popout = (person_is_noteworthy) ? 3 : 0;
  const arc_params = {
    innerRadius: radius - constants.NODE_THICKNESS,
    outerRadius: radius + radius_popout,
    startAngle: person.start_angle,
    endAngle: person.end_angle,
  };
  const new_d_visible = arcPathGenerator(arc_params);

  // See if angles have changed
  const angles = `${person.start_angle} ${person.end_angle}`;
  const prev_angles = usePrevious(angles);

  // This component has animations but the length of the animation (or
  // whether we animate at all) depends on what changed. Use duration 0
  // for no animation.
  let duration;
  if (prev_angles === angles) {
    // Don't animate this change since it's just that
    // person_is_noteworthy has changed
    duration = 0;
  } else {
    // Animate: fast if selected person changed, slow otherwise (i.e. a reordering)
    duration = selected_person_change ? constants.SELECTION_ANIM_DURATION : constants.REORDER_ANIM_DURATION;
  }

  const [node_ref, {d}, record_params] = useAnimated({
    attrs : {'d' : {generator : arcPathGenerator, params : arc_params}},
    duration,
    deps : [new_d_visible],
    on_start : () => {animating.current = true;},
    on_stop : () => {animating.current = false;}
  });

  // Initial animation: fly the arc in from off screen. Tween the arc params
  // rather than the 'd' string, and report each tick back to useAnimated, so
  // that a reorder interrupting this animation carries on from wherever the
  // arc has got to instead of snapping to its resting position first.
  useLayoutEffect(() => {
    const init_params = {
      innerRadius: radius_off_screen,
      outerRadius: radius_off_screen + constants.NODE_THICKNESS + radius_popout,
      startAngle: person.start_angle,
      endAngle: person.end_angle,
    };
    const interpolator = d3Interpolate(init_params, arc_params);
    // Record synchronously too - there is a delay before the first tick, and an
    // interruption during it would otherwise find no recorded position.
    record_params('d', init_params);
    d3Select(node_ref.current)
      .attr('d', arcPathGenerator(init_params))
      .transition()
      .duration(constants.NODELABEL_INITIAL_ANIM_DURATION)
      .delay(initial_animation_delay * constants.NODELABEL_INITIAL_ANIM_DELAY_RANGE)
      .ease(d3ease.easeBackOut)
      .attrTween('d', () => (t) => {
        const params = interpolator(t);
        record_params('d', params);
        return arcPathGenerator(params);
      })
    }, []);

  return (
    <path key={`visible_${person.person_index}`}
          className="node_visible"
          ref={node_ref}
          data-person_index={person.person_index}
          d={d}
          fill={fill}/>
  );
};

export default NodeVisible;
